"use client";
import React, { useEffect, useState, useLayoutEffect } from "react";
import useAuth from "@/utils/hooks/login-auth";
import { useRouter } from "next/router";
import useNextLesson from "@/helpers/jw-helpers/next-lesson";
import updateUserActivity from "@/utils/update-user-activity";
import { useAssessmentData, useCoursesStore } from "@/context/store";
import { CircularProgress, QuizSubmit } from "@/helpers/assessment_module";
import { getLocalStorageData } from "@/helpers/local-storage";
import { useMutation } from "@tanstack/react-query";
import { BSCustomPopover, BSToggle } from "../modal/custom-popover";
import { LearningPathLevelIcon } from "../svgcomponents/lp-common-icons";
import { formatDate } from "@/utils/convert-date";

export default function Quiz({
  assessment_test,
  assessmentName,
  courseUid,
  isStandaloneCourse,
  lessonUid,
  data,
  videoEntry,
  videoConfig,
  courseUrl,
  progress,
}) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const { courses } = useCoursesStore();
  const router = useRouter();
  const [quizData, setQuizData] = useState(null);
  const learningPathUrl = router.query?.learningPathway;
  const { quiz, setQuiz, setSubmittedQuiz, clearQuiz } = useAssessmentData();
  const { isAuthenticated } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(true);
  const [correctAnswers, setCorrectAnswers] = useState(null);

  const currentCourseIndex = courses.findIndex(
    (course) => course.uid === courseUid
  );
  const quizQuestions = assessment_test.assessment
    .sort(() => Math.random() - 0.5)
    .slice(0, assessment_test.question_count);

  useLayoutEffect(() => {
    setQuizData({
      ...assessment_test,
      assessment: quizQuestions,
    });
  }, []);

  const quizSubmit = useMutation({
    mutationKey: ["quizSubmit", lessonUid],
    mutationFn: async (formData) =>
      QuizSubmit({
        lessonUid,
        courseUid,
        formData,
      }),
    onSuccess: (data) => {
      setTimeout(() => {
        setResult(data);
        setShowResult(true);
      }, 2000);
    },
  });

  useEffect(() => {
    if (progress?.progress?.assessment?.attemptLog) {
      setShowResult(true);
      setResult(progress?.progress?.assessment?.attemptLog.at(-1));
    }
  }, [progress]);

  useEffect(() => {
    if (isAuthenticated == false) {
      router.push("/login");
    }
  }, [isAuthenticated]);

  const user = getLocalStorageData("user");

  // Handle focus and route changes
  useEffect(() => {
    // Clear quiz state when route changes
    clearQuiz();
    setQuestionIndex(0);
    setResult(null);
  }, [lessonUid]);

  useEffect(() => {
    if (quiz[questionIndex]?.isSubmitted) {
      const correctOptions = findCorrectAnswers(
        quizData?.assessment[questionIndex]
      );
      const isInCorrect =
        questionType === "multiple_choice_questions"
          ? JSON.stringify(correctOptions.sort()) !==
            JSON.stringify(quiz[questionIndex]?.selected_options.sort())
          : correctOptions !== quiz[questionIndex]?.selected_options;

      if (isInCorrect) {
        questionType === "multiple_choice_questions"
          ? setCorrectAnswers(correctOptions.join(", "))
          : setCorrectAnswers(correctOptions);
      } else {
        setCorrectAnswers(null);
      }
    } else {
      setCorrectAnswers(null);
    }

    // Check if all quiz items have been submitted

    const allQuestionsSubmitted =
      quiz.every((item) => item?.isSubmitted === true) &&
      quiz.length === quizData?.question_count;

    const formData = quiz.map((item) => {
      return {
        question_uid: item?.question_uid,
        selected_options: item?.selected_options,
      };
    });

    if (!result && allQuestionsSubmitted) {
      quizSubmit.mutate(formData);
    }
  }, [quiz, questionIndex]);

  const currentLessonIndex = courses[
    currentCourseIndex
  ]?.chapters[0]?.lessons?.lessons.findIndex(
    (lesson) => lesson.uid === lessonUid
  );

  let pathname = router.asPath.replace(/\?.*$/, "");
  const nextLesson = useNextLesson(videoConfig, router, true);
  const currentLessonName = pathname.split("/").pop();
  const currentIndex = videoConfig
    ? Object.values(videoConfig).findIndex(
        (x) => x.url.split("/").pop() === currentLessonName
      )
    : 0;
  const isLast = currentIndex === Object?.keys(videoConfig || "")?.length - 1;

  const handleReload = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };
  const NextCourse = async () => {
    const userData = {
      courseEntryUid: data?.uid,
      lessonEntryUid: videoEntry?.uid,
      courseActivity: {
        progress: {
          percentage: 100,
          isCompleted: true,
        },
      },
      lessonActivity: {
        progress: {
          percentage: 100,
          isCompleted: true,
        },
      },
    };
    if (isAuthenticated) {
      await updateUserActivity(userData, isStandaloneCourse).then((res) => {
        if (res.status === 200) {
          isLast ? handleReload() : nextLesson();
        }
      });
    } else {
      nextLesson();
    }
  };
  const handleLastCourse = () => {
    window.location.href = `/academy/learning-paths/${learningPathUrl}${courses[currentCourseIndex + 1]?.url}`;
  };
  const questionType =
    quizData?.assessment && Object.keys(quizData?.assessment[questionIndex])[0];

  const isNormalCourseWithoutLearningPath = (
    router.basePath + router.asPath
  ).includes("/academy/courses");

  return (
    <>
      <div className={"border-b-1"}>
        <div className="container mw-1920">
          <a
            role="button"
            aria-label="Back To Course"
            onClick={() => BSToggle("leave-popup")}
            className="outline-focussed fw-semi-bold text-center px-2 my-3 d-flex align-items-center text-primary "
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.1875 12C3.1875 11.6893 3.43934 11.4375 3.75 11.4375H20.25C20.5607 11.4375 20.8125 11.6893 20.8125 12C20.8125 12.3107 20.5607 12.5625 20.25 12.5625H3.75C3.43934 12.5625 3.1875 12.3107 3.1875 12Z"
                fill="#6C5CE7"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.8977 4.85225C11.1174 5.07192 11.1174 5.42808 10.8977 5.64775L4.54549 12L10.8977 18.3523C11.1174 18.5719 11.1174 18.9281 10.8977 19.1477C10.6781 19.3674 10.3219 19.3674 10.1023 19.1477L3.35225 12.3977C3.13258 12.1781 3.13258 11.8219 3.35225 11.6023L10.1023 4.85225C10.3219 4.63258 10.6781 4.63258 10.8977 4.85225Z"
                fill="#6C5CE7"
              />
            </svg>
            <span className="ms-2">Back To Course</span>
          </a>
        </div>
      </div>
      <div
        id="quiz"
        className="d-flex w-100 gap-4 flex-column justify-content-start align-items-start mw-1920 py-5 learning-path__container px-3 px-md-4 px-lg-5"
      >
        {(!result || !showResult) && (
          <>
            <div className="d-flex w-100 flex-column flex-md-row justify-content-between align-items-start gap-4">
              <div className="d-flex flex-column gap-2 w-100 w-md-auto">
                <h1 className="text-grayish-blue fs-28 fw-semi-bold">
                  {assessmentName} Quiz
                </h1>
                <div className="text-gray fs-12 d-flex flex-row flex-wrap gap-3 small fw-normal mt-2">
                  <p className="fs-12 lh-150">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M4 3.37578C3.65482 3.37578 3.375 3.6556 3.375 4.00078V13.0008C3.375 13.346 3.65482 13.6258 4 13.6258H12C12.3452 13.6258 12.625 13.346 12.625 13.0008V4.00078C12.625 3.6556 12.3452 3.37578 12 3.37578H10.1528V2.62578H12C12.7594 2.62578 13.375 3.24139 13.375 4.00078V13.0008C13.375 13.7602 12.7594 14.3758 12 14.3758H4C3.24061 14.3758 2.625 13.7602 2.625 13.0008V4.00078C2.625 3.24139 3.24061 2.62578 4 2.62578H5.84722V3.37578H4Z"
                        fill="#475161"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.175 3.00078C5.175 2.269 5.76822 1.67578 6.5 1.67578H9.5C10.2318 1.67578 10.825 2.269 10.825 3.00078C10.825 3.73256 10.2318 4.32578 9.5 4.32578H6.5C5.76822 4.32578 5.175 3.73256 5.175 3.00078ZM6.5 2.32578C6.12721 2.32578 5.825 2.62799 5.825 3.00078C5.825 3.37357 6.12721 3.67578 6.5 3.67578H9.5C9.87279 3.67578 10.175 3.37357 10.175 3.00078C10.175 2.62799 9.87279 2.32578 9.5 2.32578H6.5Z"
                        fill="#475161"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.125 6.00078C5.125 5.79367 5.29289 5.62578 5.5 5.62578H10.5C10.7071 5.62578 10.875 5.79367 10.875 6.00078C10.875 6.20789 10.7071 6.37578 10.5 6.37578H5.5C5.29289 6.37578 5.125 6.20789 5.125 6.00078Z"
                        fill="#475161"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.125 11.0008C5.125 10.7937 5.29289 10.6258 5.5 10.6258H10.5C10.7071 10.6258 10.875 10.7937 10.875 11.0008C10.875 11.2079 10.7071 11.3758 10.5 11.3758H5.5C5.29289 11.3758 5.125 11.2079 5.125 11.0008Z"
                        fill="#475161"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.125 8.50078C5.125 8.29367 5.29289 8.12578 5.5 8.12578H10.5C10.7071 8.12578 10.875 8.29367 10.875 8.50078C10.875 8.70789 10.7071 8.87578 10.5 8.87578H5.5C5.29289 8.87578 5.125 8.70789 5.125 8.50078Z"
                        fill="#475161"
                      />
                    </svg>
                    {quizData?.question_count} Questions
                  </p>{" "}
                  |{" "}
                  <p className="fs-12 lh-150">
                    Released: {formatDate(videoEntry?.updated_at)}
                  </p>
                </div>
              </div>
              <div className="progress-bar bg-white">
                <div>
                  <span
                    style={{
                      width: "auto",
                      minWidth: `${(quiz.length / quizData?.question_count) * 100}%`,
                      transition: "min-width 0.3s ease-in-out",
                    }}
                  ></span>
                </div>
                <p className="lh-150">
                  {quiz.length + "/" + quizData?.question_count} answered
                </p>
              </div>
            </div>

            {/* =====================Quiz Box================ */}

            <form
              className="w-100 d-flex flex-column gap-5"
              id="quiz-form"
            >
              {/* ================Question Box=================== */}
              <div
                key={
                  quizData?.assessment[questionIndex][questionType]._metadata
                    .uid
                }
                className="d-flex flex-column border rounded p-3"
              >
                {/* ============Question Number================ */}
                <h2 className="text-muted fs-6 fw-normal mb-2">
                  Question {questionIndex + 1} of {quizData?.question_count}
                </h2>
                {/* ================Question=================== */}
                <h3 className="fs-5 fw-normal mb-4 select-none">
                  {quizData?.assessment[questionIndex][questionType].question}
                </h3>

                {/* ================Options=================== */}

                <div className="d-flex flex-column gap-4">
                  <div className={`d-flex gap-4 flex-column `}>
                    {/* ========================Multiple Select============================== */}

                    {questionType === "multiple_choice_questions" && (
                      <>
                        {quizData?.assessment[questionIndex][
                          questionType
                        ].test_options.map((option, qi) => {
                          return (
                            <div
                              key={
                                qi +
                                quizData?.assessment[questionIndex][
                                  questionType
                                ]._metadata.uid
                              }
                              className="form-check"
                              role="group"
                            >
                              <input
                                aria-label={option.option}
                                role="checkbox"
                                className={`form-check-input cursor-pointer outline-focussed ${
                                  typeof correctAnswers === "string" &&
                                  (correctAnswers.includes(",")
                                    ? !correctAnswers
                                        .split(", ")
                                        .includes(option.option)
                                    : correctAnswers !== option.option)
                                    ? "wrong-answer"
                                    : quiz[questionIndex]?.isSubmitted &&
                                      "right-answer"
                                }`}
                                type="checkbox"
                                id={option.option + questionIndex}
                                name={
                                  quizData?.assessment[questionIndex][
                                    questionType
                                  ]._metadata.uid
                                }
                                value={option.option}
                                required={
                                  questionType !== "multiple_choice_questions"
                                }
                                onChange={async (e) => {
                                  const value = e.target.value;
                                  const isChecked = e.target.checked;
                                  const currentOptions =
                                    quiz[questionIndex]?.selected_options || [];

                                  const updatedOptions = isChecked
                                    ? [...currentOptions, value]
                                    : currentOptions.filter(
                                        (option) => option !== value
                                      );

                                  setQuiz({
                                    question_uid:
                                      quizData?.assessment[questionIndex][
                                        questionType
                                      ]._metadata.uid,
                                    selected_options: updatedOptions,
                                    qindex: questionIndex,
                                    isSubmitted: false,
                                  });
                                }}
                                checked={quiz[
                                  questionIndex
                                ]?.selected_options?.includes(option.option)}
                                disabled={quiz[questionIndex]?.isSubmitted}
                              />

                              <label
                                className={`form-check-label cursor-pointer `}
                                htmlFor={option.option + questionIndex}
                              >
                                {option.option}
                                {quiz[questionIndex]?.isSubmitted &&
                                  ((!correctAnswers &&
                                    quiz[
                                      questionIndex
                                    ]?.selected_options?.includes(
                                      option.option
                                    )) ||
                                    correctAnswers) && (
                                    <>
                                      <br />
                                      <p
                                        className={`fs-14 m-0 p-0 mt-2 lh-150 ${
                                          typeof correctAnswers === "string" &&
                                          (correctAnswers.includes(",")
                                            ? !correctAnswers
                                                .split(", ")
                                                .includes(option.option)
                                            : correctAnswers !== option.option)
                                            ? "text-red"
                                            : quiz[questionIndex]
                                                ?.isSubmitted && "text-green"
                                        }`}
                                      >
                                        {correctAnswers
                                          ? correctAnswers.includes(",")
                                            ? correctAnswers
                                                .split(", ")
                                                .includes(option.option)
                                              ? "Correct"
                                              : "Incorrect"
                                            : correctAnswers !== option.option
                                              ? "Incorrect"
                                              : "Correct"
                                          : quiz[questionIndex]?.isSubmitted &&
                                            "Correct"}
                                      </p>
                                    </>
                                  )}
                              </label>
                            </div>
                          );
                        })}
                        {quiz[questionIndex]?.isSubmitted && correctAnswers && (
                          <div className="correct-answer position-relative">
                            <p className="text-muted lh-150">
                              The correct answer was:
                            </p>
                            <p className="fs-5 lh-150">{correctAnswers}</p>
                          </div>
                        )}
                      </>
                    )}
                    {/* ========================Single Select============================== */}
                    {questionType === "single_choice_questions" && (
                      <>
                        {quizData?.assessment[questionIndex][
                          questionType
                        ].test_options.map((option, qi) => {
                          return (
                            <>
                              <div
                                key={
                                  qi +
                                  quizData?.assessment[questionIndex][
                                    questionType
                                  ]._metadata.uid
                                }
                                className="form-check"
                                role="group"
                              >
                                <input
                                  aria-label={option}
                                  role="radio"
                                  type="radio"
                                  className={`form-check-input cursor-pointer outline-focussed ${correctAnswers ? "wrong-answer" : quiz[questionIndex]?.isSubmitted && "right-answer"} `}
                                  name={
                                    quizData?.assessment[questionIndex][
                                      questionType
                                    ]._metadata.uid
                                  }
                                  disabled={quiz[questionIndex]?.isSubmitted}
                                  id={option + questionIndex}
                                  value={option}
                                  required={true}
                                  checked={
                                    quiz[questionIndex]?.selected_options ===
                                    option
                                  }
                                  onChange={(e) => {
                                    e.target.value &&
                                      setQuiz({
                                        question_uid:
                                          quizData?.assessment[questionIndex][
                                            questionType
                                          ]._metadata.uid,
                                        selected_options: e.target.value,
                                        qindex: questionIndex,
                                        isSubmitted: false,
                                      });
                                  }}
                                  autoComplete="off"
                                />
                                <label
                                  className={`form-check-label cursor-pointer `}
                                  htmlFor={option + questionIndex}
                                >
                                  {option}
                                  {quiz[questionIndex]?.isSubmitted &&
                                    quiz[questionIndex]?.selected_options ===
                                      option && (
                                      <>
                                        <br />
                                        <p
                                          className={`fs-14 m-0 p-0 mt-2 lh-150 ${
                                            correctAnswers
                                              ? "text-red"
                                              : quiz[questionIndex]
                                                  ?.isSubmitted && "text-green"
                                          }`}
                                        >
                                          {correctAnswers
                                            ? "Incorrect"
                                            : quiz[questionIndex]
                                                ?.isSubmitted && "Correct"}
                                        </p>
                                      </>
                                    )}
                                </label>
                              </div>
                            </>
                          );
                        })}
                        {quiz[questionIndex]?.isSubmitted && correctAnswers && (
                          <div className="correct-answer position-relative">
                            <p className="text-muted lh-150">
                              The correct answer was:
                            </p>
                            <p className="fs-5 lh-150">{correctAnswers}</p>
                          </div>
                        )}
                      </>
                    )}

                    {/* ========================True or False============================== */}
                    {questionType === "true_or_false" && (
                      <>
                        {[true, false].map((option, index) => {
                          return (
                            <div
                              className="form-check"
                              role="group"
                              key={
                                quizData?.assessment[questionIndex][
                                  questionType
                                ]._metadata.uid + option
                              }
                            >
                              <input
                                aria-label={option.toString()}
                                role="radio"
                                type="radio"
                                className={`form-check-input cursor-pointer outline-focussed outline-focussed ${typeof correctAnswers === "boolean" ? "wrong-answer" : quiz[questionIndex]?.isSubmitted && "right-answer"}`}
                                disabled={quiz[questionIndex]?.isSubmitted}
                                name={
                                  quizData?.assessment[questionIndex][
                                    questionType
                                  ]._metadata.uid
                                }
                                value={option}
                                id={
                                  quizData?.assessment[questionIndex][
                                    questionType
                                  ]._metadata.uid + index
                                }
                                checked={
                                  quiz[
                                    questionIndex
                                  ]?.selected_options?.toString() ===
                                  option.toString()
                                }
                                onChange={(e) => {
                                  e.target.value &&
                                    setQuiz({
                                      question_uid:
                                        quizData?.assessment[questionIndex][
                                          questionType
                                        ]._metadata.uid,
                                      selected_options:
                                        e.target.value === "true",
                                      qindex: questionIndex,
                                      isSubmitted: false,
                                    });
                                }}
                                required
                                autoComplete="off"
                              />
                              <label
                                className="form-check-label cursor-pointer text-capitalize"
                                htmlFor={
                                  quizData?.assessment[questionIndex][
                                    questionType
                                  ]._metadata.uid + index
                                }
                              >
                                {option.toString()}
                                {quiz[questionIndex]?.isSubmitted &&
                                  quiz[
                                    questionIndex
                                  ]?.selected_options?.toString() ===
                                    option.toString() && (
                                    <>
                                      <br />
                                      <p
                                        className={`fs-14 m-0 p-0 mt-2 lh-150 ${
                                          typeof correctAnswers === "boolean"
                                            ? "text-red"
                                            : quiz[questionIndex]
                                                ?.isSubmitted && "text-green"
                                        }`}
                                      >
                                        {typeof correctAnswers === "boolean"
                                          ? "Incorrect"
                                          : quiz[questionIndex]?.isSubmitted &&
                                            "Correct"}
                                      </p>
                                    </>
                                  )}
                              </label>
                            </div>
                          );
                        })}
                        {quiz[questionIndex]?.isSubmitted &&
                          correctAnswers !== null && (
                            <div className="correct-answer position-relative">
                              <p className="text-muted lh-150">
                                The correct answer was:
                              </p>
                              <p className="fs-5 lh-150">
                                {correctAnswers ? "True" : "False"}
                              </p>
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* ================Navigation Buttons=================== */}
              <div className="d-flex flex-wrap flex-column-reverse flex-md-row gap-3">
                {/* =========Previous Button========== */}
                {questionIndex > 0 && (
                  <button
                    aria-label="Previous Question"
                    role="button"
                    className="admy-primary-btn underline-focussed radius-4px admy-btn-hover-underline w-12 mx-auto mx-md-0 border-0 bg-white"
                    disabled={isSubmitting}
                    type={"button"}
                    onClick={() => {
                      setQuestionIndex(questionIndex - 1);
                    }}
                  >
                    Previous Question
                  </button>
                )}

                {/* =========Next Button========== */}
                {quiz[questionIndex]?.isSubmitted &&
                  questionIndex < quizData?.assessment.length - 1 && (
                    <button
                      aria-label="Next Question"
                      role="button"
                      className={`admy-primary-btn radius-4 btn-w-160 outline-focussed ${result ? "admy-btn-hover-underline mx-auto mx-md-0 border-0 bg-white" : ""}`}
                      disabled={isSubmitting}
                      type={"button"}
                      onClick={() => {
                        setQuestionIndex(questionIndex + 1);
                      }}
                    >
                      Next Question
                    </button>
                  )}

                {/* =========Submit Button========== */}
                {(!quiz[questionIndex]?.isSubmitted || result) && (
                  <button
                    aria-label={result ? "Show Result" : "Submit"}
                    role="button"
                    className="admy-primary-btn radius-4  btn-w-160 outline-focussed"
                    disabled={
                      (!quiz[questionIndex] ||
                        quiz[questionIndex]?.isSubmitted) &&
                      showResult
                    }
                    type={"submit"}
                    onClick={() => {
                      result
                        ? setShowResult(!showResult)
                        : setSubmittedQuiz(questionIndex);
                    }}
                  >
                    {!result ? "Submit" : "Show Result"}
                  </button>
                )}
              </div>
            </form>
          </>
        )}
        {/* ====================Result========================= */}
        {result && showResult && (
          <div className="w-100 pb-5">
            <div className="row justify-content-center">
              <div className="col-12 col-lg-10">
                <div className="card assessment-card mb-4 p-4">
                  <div className="card-body text-center">
                    <h1 className="mb-2 fs-4 fs-md-3">
                      {(result?.percentageAchieved >=
                        assessment_test?.passing_criteria ?? 75)
                        ? `Congrats, ${user.firstName}! You Passed`
                        : "Keep Going! Review And Retry."}
                    </h1>
                    <p className="text-muted mb-1 lh-150">
                      {(result?.percentageAchieved >=
                        assessment_test?.passing_criteria ?? 75)
                        ? `You completed the ${assessmentName} quiz
                        path. Keep up the great work!`
                        : "Review your answers and try again when you're ready."}
                    </p>
                    <p className="text-muted small lh-150">
                      Completed on{" "}
                      {new Date().toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>

                    <div className="my-4">
                      <CircularProgress
                        percentage={result?.percentageAchieved}
                        passing_percentage={
                          assessment_test?.passing_criteria ?? 75
                        }
                      />
                      <p className="text-muted small mt-2 lh-150">Score</p>
                      {(result?.percentageAchieved ??
                        result?.maxPercentageAchieved) <
                        (assessment_test?.passing_criteria ?? 75) && (
                        <p className="text-muted small m-0 lh-150">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 17 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8.5 16C12.6421 16 16 12.6421 16 8.5C16 4.35786 12.6421 1 8.5 1C4.35786 1 1 4.35786 1 8.5C1 12.6421 4.35786 16 8.5 16Z"
                              fill="#0469E3"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M8.5 1.46875C4.61675 1.46875 1.46875 4.61675 1.46875 8.5C1.46875 12.3833 4.61675 15.5312 8.5 15.5312C12.3833 15.5312 15.5312 12.3833 15.5312 8.5C15.5312 4.61675 12.3833 1.46875 8.5 1.46875ZM0.53125 8.5C0.53125 4.09898 4.09898 0.53125 8.5 0.53125C12.901 0.53125 16.4688 4.09898 16.4688 8.5C16.4688 12.901 12.901 16.4688 8.5 16.4688C4.09898 16.4688 0.53125 12.901 0.53125 8.5Z"
                              fill="#475161"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M8.5 4.73505C8.75888 4.73505 8.96875 4.94491 8.96875 5.2038V9.5788C8.96875 9.83768 8.75888 10.0475 8.5 10.0475C8.24112 10.0475 8.03125 9.83768 8.03125 9.5788V5.2038C8.03125 4.94491 8.24112 4.73505 8.5 4.73505Z"
                              fill="white"
                            />
                            <path
                              d="M9.28125 12.1426C9.28125 12.5741 8.93147 12.9238 8.5 12.9238C8.06853 12.9238 7.71875 12.5741 7.71875 12.1426C7.71875 11.7111 8.06853 11.3613 8.5 11.3613C8.93147 11.3613 9.28125 11.7111 9.28125 12.1426Z"
                              fill="white"
                            />
                          </svg>{" "}
                          You need at least{" "}
                          <strong>
                            {assessment_test?.passing_criteria ?? 75}%
                          </strong>{" "}
                          to pass
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-12 col-md-4">
                    <div className="card assessment-card stats-card h-100">
                      <div className="stats-number">{result.totalMarks}</div>
                      <div className="stats-label">Correct Answers</div>
                      <div className="stats-sublabel">
                        Out of {result.totalQuestions} questions
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="card assessment-card stats-card h-100">
                      <div className="stats-number">
                        {result.totalQuestions - result.totalMarks}
                      </div>
                      <div className="stats-label">Incorrect Answers</div>
                      <div className="stats-sublabel">
                        Out of {result.totalQuestions} questions
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="card assessment-card stats-card h-100">
                      <div className="stats-number">
                        {result?.totalAttempts || result.attemptNumber || 1}
                        <sup className="fs-6">
                          {(result?.totalAttempts || result.attemptNumber) === 1
                            ? "st"
                            : (result?.totalAttempts ||
                                  result.attemptNumber) === 2
                              ? "nd"
                              : (result?.totalAttempts ||
                                    result.attemptNumber) === 3
                                ? "rd"
                                : "th"}
                        </sup>
                      </div>
                      <div className="stats-label">Attempt</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row mt-5 d-flex flex-column-reverse flex-md-row gap-4 justify-content-center">
              {quiz.length > 0 && (
                <button
                  className="admy-primary-btn radius-4 admy-btn-white border-0 pe-0 bg-white outline-focussed"
                  type={"button"}
                  onClick={() => {
                    setQuestionIndex(0);
                    setShowResult(false);
                  }}
                  aria-label="Review All Answers"
                >
                  Review All Answers
                </button>
              )}
              <button
                className="admy-primary-btn radius-4 admy-outline-btn btn-w-160 outline-focussed"
                type={"button"}
                aria-label="Retake Quiz"
                onClick={() => {
                  setResult(null);
                  clearQuiz();
                  setQuestionIndex(0);
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.9241 5.15155C18.0731 5.70769 17.7431 6.27933 17.187 6.42835L14.4756 7.15485C14.1195 7.25026 13.7535 7.03894 13.6581 6.68285C13.5627 6.32677 13.774 5.96075 14.1301 5.86534L15.8567 5.40269C14.3674 4.57733 12.6488 4.24966 10.9537 4.4744C9.12087 4.7174 7.42987 5.59101 6.17112 6.94519C4.91237 8.29937 4.16444 10.0496 4.05579 11.8953C3.94714 13.7409 4.48456 15.5668 5.57573 17.0593C6.6669 18.5518 8.24373 19.6178 10.0354 20.0742C11.827 20.5305 13.7217 20.3488 15.3939 19.5601C17.0661 18.7715 18.4115 17.4252 19.199 15.7524C19.9866 14.0797 20.1671 12.1849 19.7095 10.3936C19.6183 10.0364 19.8339 9.67291 20.1911 9.58168C20.5483 9.49045 20.9118 9.70604 21.003 10.0632C21.5373 12.1551 21.3265 14.3677 20.4069 16.3211C19.4872 18.2745 17.9161 19.8466 15.9633 20.7676C14.0106 21.6885 11.7981 21.9008 9.70585 21.3678C7.61363 20.8349 5.77227 19.5901 4.49803 17.8472C3.2238 16.1043 2.59622 13.9721 2.7231 11.8168C2.84997 9.6615 3.72338 7.61765 5.19331 6.03628C6.66323 4.45492 8.63793 3.43475 10.7782 3.15098C12.6666 2.90063 14.5798 3.23729 16.2617 4.10549L15.9081 2.78574C15.8127 2.42965 16.024 2.06364 16.3801 1.96822C16.7362 1.87281 17.1022 2.08413 17.1976 2.44022L17.9241 5.15155Z"
                    fill="currentColor"
                  />
                </svg>{" "}
                Retake Quiz
              </button>
              {(courses[currentCourseIndex + 1]?.url ||
                courses[currentCourseIndex]?.chapters[0]?.lessons?.lessons[
                  currentLessonIndex + 1
                ]?.url) && (
                <button
                  className="admy-primary-btn radius-4 btn-w-160 outline-focussed"
                  type={"button"}
                  aria-label={
                    isNormalCourseWithoutLearningPath
                      ? "Back To Course"
                      : isLast
                        ? "Next Course"
                        : "Next Lesson"
                  }
                  onClick={() => {
                    {
                      setQuestionIndex(0);
                      clearQuiz();
                      setResult(null);
                      if (isNormalCourseWithoutLearningPath) {
                        window.location.href = "/academy/courses" + courseUrl;
                      } else {
                        isLast ? handleLastCourse() : NextCourse();
                      }
                    }
                  }}
                >
                  {isNormalCourseWithoutLearningPath
                    ? "Back To Course"
                    : isLast
                      ? "Next Course"
                      : "Next Lesson"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <BSCustomPopover
        id="leave-popup"
        header="Leave Quiz"
        body={`Are you sure you want to leave?`}
        maxWidth="600px"
        footerBtns={[
          {
            label: "Go Back",
            isToggle: true,
            id: "leave-popup",
            className: "admy-outline-btn border-0",
          },
          {
            label: "Yes, Leave",
            isToggle: true,
            id: "leave-popup",
            onClick: () => {
              if (pathname.includes("learning-paths")) {
                window.location.href =
                  "/academy/learning-paths/" + learningPathUrl + courseUrl;
              } else if (pathname.includes("courses")) {
                window.location.href = "/academy/courses" + courseUrl;
              }
            },
          },
        ]}
      />
    </>
  );
}

function findCorrectAnswers(question) {
  if (!question) {
    return null;
  }

  const questionType = Object.keys(question)[0];

  switch (questionType) {
    case "single_choice_questions":
      // Return the correct answer for single choice questions based on quid index
      const quidToIndexMap = {
        "Beg7/hBKyrbkHJBSlJx03Q==": 0,
        "6rB7P7l3VHbHaQq7KSD++A==": 1,
        "mgPfTX7Vj06gpJAxzlamnQ==": 2,
        "eL2v6J85yBew3PzSBFj3Mw==": 3,
      };

      const correctOptionIndex =
        quidToIndexMap[question[questionType].quid] || 0;
      return question[questionType].test_options[correctOptionIndex];

    case "multiple_choice_questions":
      // Return array of correct answers for multiple choice based on quid mapping
      return question[questionType].test_options
        .filter((option) => option.quid === "yldf1mkHjJvan/ip11Dy0g==")
        .map((option) => option.option);

    case "true_or_false":
      // Return boolean for true/false questions based on quid mapping

      return question[questionType].quid === "yldf1mkHjJvan/ip11Dy0g==";

    default:
      return null;
  }
}
